import { parseSearchIdentifier } from './identifier';
import { convertTo18CharId } from './sfdc-id';

/**
 * The classified outcome of resolving a raw path segment (or pasted search
 * string) into an entity route intent. This is the ONLY id→intent classifier
 * for the short entity entry routes (`/account/:id`, `/contact/:id`, `/:id`)
 * — the frontend router (`web/app-home/src/components/flow/entity-routes.tsx`)
 * turns this into a `<Navigate>` and nothing else re-derives the classification.
 */
export type EntityRouteIntent =
  | { kind: 'account'; accountId: string }
  | { kind: 'contact'; contactId: string }
  | { kind: 'search'; query: string };

/**
 * Classify a raw path segment into an entity route intent, normalizing any
 * recognized SFDC id to its 18-char form (plan D2 — redirect URLs must never
 * carry a 15-char id).
 *
 * `email` and `domain` shapes (and anything unrecognized) fall through to
 * `{kind: 'search'}` rather than being resolved here: the omnibox
 * (`GlobalSearch`) already knows how to resolve those via its own
 * `/api/flow/lookup` call, so there is no reason to duplicate that lookup at
 * the routing layer — the short route just hands the raw query to the
 * Command Center search box and lets it do the resolution it already does.
 */
export function resolveEntityRoute(raw: string): EntityRouteIntent {
  const parsed = parseSearchIdentifier(raw);

  if (parsed.kind === 'sfdc_account_id') {
    return { kind: 'account', accountId: convertTo18CharId(parsed.value) };
  }

  if (parsed.kind === 'sfdc_contact_id') {
    return { kind: 'contact', contactId: convertTo18CharId(parsed.value) };
  }

  return { kind: 'search', query: raw.trim() };
}
